import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import * as NavigationMenu from '@radix-ui/react-navigation-menu';

interface NavItem {
  title: string;
  href?: string;
  scrollTo?: string;
  external?: boolean;
  description: string;
  icon: string;
}

const solutionsItems: NavItem[] = [
  // Industry
  {
    title: 'Subscriptions',
    href: '/industry/subscriptions',
    description: 'For SaaS, streaming, and membership platforms losing 10% of ARR to failed payments.',
    icon: 'ph-repeat',
  },
  {
    title: 'E-Commerce',
    href: '/industry/e-commerce',
    description: 'For online retailers where 62% of customers hit by payment errors never return.',
    icon: 'ph-storefront',
  },
];

const paymentMethodItems: NavItem[] = [
  {
    title: 'Credit & debit cards',
    href: '/solutions/payment-method/cards',
    description: '85% of subscription revenue rides on cards. Per-issuer timing, network tokens, AU, the works.',
    icon: 'ph-credit-card',
  },
  {
    title: 'Direct debit (ACH · SEPA · BACS)',
    href: '/solutions/payment-method/direct-debit',
    description: 'Pay-cycle aware retries for bank-to-bank pulls with multi-day settlement.',
    icon: 'ph-bank',
  },
  {
    title: 'Digital wallets',
    href: '/solutions/payment-method/digital-wallets',
    description: 'Apple Pay and Google Pay as a first-class recovery rail, not a fallback.',
    icon: 'ph-wallet',
  },
  {
    title: 'PayPal',
    href: '/solutions/payment-method/paypal',
    description: 'Billing Agreements, Reference Transactions, and PayPal-native error mapping.',
    icon: 'ph-currency-circle-dollar',
  },
];

const failureReasonItems: NavItem[] = [
  {
    title: 'insufficient_funds',
    href: '/solutions/failure-reason/insufficient-funds',
    description: 'The biggest soft-decline category - and the most recoverable. Payday-anchored timing.',
    icon: 'ph-piggy-bank',
  },
  {
    title: 'Soft declines',
    href: '/solutions/failure-reason/soft-declines',
    description: 'The recoverable middle band. Per-reason strategies across 60-75% of failures.',
    icon: 'ph-arrow-clockwise',
  },
  {
    title: 'Hard declines',
    href: '/solutions/failure-reason/hard-declines',
    description: 'Stop retrying. Convert dead cards into one-tap customer-side updates.',
    icon: 'ph-x-circle',
  },
  {
    title: 'do_not_honor',
    href: '/solutions/failure-reason/do-not-honor',
    description: 'The most common - and most opaque - decline code globally. Issuer-specific timing.',
    icon: 'ph-question',
  },
  {
    title: 'Expired cards',
    href: '/solutions/failure-reason/expired-cards',
    description: 'Pre-emptive Account Updater sweeps + network tokens + one-tap update fallback.',
    icon: 'ph-calendar-x',
  },
];

const toolsItems: NavItem[] = [
  {
    title: 'BIN Lookup',
    href: '/tools/bin-lookup',
    description: 'Identify the issuing bank, country, and card type for any 4 to 8 digit BIN. Free.',
    icon: 'ph-bank',
  },
  {
    title: 'Decline Codes',
    href: '/tools/decline-codes',
    description: 'Searchable dictionary of payment decline codes across every major gateway, with plain-English meanings.',
    icon: 'ph-list-magnifying-glass',
  },
];

const companyItems: NavItem[] = [
  {
    title: 'About Us',
    href: '/about',
    description: 'Our mission, principles, and what we stand for.',
    icon: 'ph-buildings',
  },
  {
    title: 'Blog',
    href: '/blog',
    description: 'Newsletter and insights on revenue recovery and involuntary churn.',
    icon: 'ph-newspaper',
  },
  {
    title: 'FAQ',
    scrollTo: 'section-faq',
    description: 'Common questions about Revtain, pricing, and integration.',
    icon: 'ph-question',
  },
];

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const handleLogoClick = () => {
    if (isHomePage) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      navigate('/');
    }
  };

  const scrollToSection = (e: React.MouseEvent, sectionId: string) => {
    e.preventDefault();
    if (isHomePage) {
      const section = document.getElementById(sectionId);
      if (section) {
        const navbarHeight = 80;
        const sectionTop = section.getBoundingClientRect().top + window.pageYOffset - navbarHeight;
        window.scrollTo({ top: sectionTop, behavior: 'smooth' });
      }
    } else {
      navigate(`/#${sectionId}`);
    }
    setIsMobileMenuOpen(false);
  };

  const handleNavItemClick = (e: React.MouseEvent, item: NavItem) => {
    if (item.scrollTo) {
      scrollToSection(e, item.scrollTo);
    } else if (item.href && !item.external) {
      e.preventDefault();
      navigate(item.href);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const isActive = (path: string) => location.pathname === path;
  const isActiveGroup = (paths: string[]) => paths.some(p => location.pathname.startsWith(p));

  // Light-theme nav for every light-background page, so white nav text is
  // never washed out on a pale hero. Dark-themed pages (/, /about, /blog,
  // /industry/*, /tools/*) keep the transparent white-text nav; legal pages
  // paint their own dark strip behind the bar.
  const LIGHT_THEME_ROUTES = ['/solutions', '/integrations', '/evaluation', '/compare'];
  const isLightTheme = LIGHT_THEME_ROUTES.some((r) => location.pathname.startsWith(r));
  const navText = isLightTheme ? 'text-[#3A453E]' : 'text-[#E0EEEE]';
  const navTextHover = isLightTheme ? 'hover:text-[#00713F]' : 'hover:text-[#00E599]';
  const navActive = isLightTheme ? 'text-[#00713F]' : 'text-[#00E599]';
  const mobileTriggerText = isLightTheme ? 'text-[#0A1A0F]' : 'text-[#E0EEEE]';

  const navBgClass = isLightTheme
    ? (isScrolled
        ? 'bg-white/95 backdrop-blur-md shadow-[0_4px_20px_-8px_rgba(0,71,49,0.15)] border-b border-[#E5EBE8]'
        : 'bg-white/85 backdrop-blur-md border-b border-[#E5EBE8]/60')
    : (isScrolled
        ? 'bg-[#0A0F0A]/95 backdrop-blur-md shadow-lg border-b border-[#E0EEEE]/10'
        : 'bg-[#0A0F0A]');

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${navBgClass}`}>
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-20 md:h-24">
          {/* Logo — same artwork both ways. The original has a white wordmark
              (for dark pages); revtain-logo-dark.png is the pixel-identical
              recolour with a dark wordmark for light pages. Same canvas, so
              identical sizing. */}
          <div className="flex items-center gap-3">
            <a
              href="/"
              onClick={(e) => {
                e.preventDefault();
                handleLogoClick();
              }}
              className="cursor-pointer transition-opacity duration-300 hover:opacity-80"
            >
              <img
                src={isLightTheme ? '/revtain-logo-dark.png' : '/Untitled design-3.png'}
                alt="Revtain Logo"
                className="h-20 sm:h-24 md:h-24 lg:h-28 w-auto"
              />
            </a>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-2">
            <NavigationMenu.Root className="relative" delayDuration={150}>
              <NavigationMenu.List className="flex items-center gap-1">
                {/* Product (scroll link) */}
                <NavigationMenu.Item>
                  <NavigationMenu.Link asChild>
                    <a
                      href="#section-product"
                      onClick={(e) => scrollToSection(e, 'section-product')}
                      className={`block px-3 py-2 text-sm font-medium ${navText} ${navTextHover} transition-colors rounded-md cursor-pointer`}
                    >
                      Product
                    </a>
                  </NavigationMenu.Link>
                </NavigationMenu.Item>

                {/* Solutions dropdown */}
                <NavigationMenu.Item className="relative">
                  <NavigationMenu.Trigger
                    className={`group flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-md transition-colors outline-none ${
                      isActiveGroup(['/industry', '/solutions']) ? navActive : `${navText} ${navTextHover}`
                    }`}
                  >
                    Solutions
                    <i className="ph ph-caret-down text-xs transition-transform duration-200 group-data-[state=open]:rotate-180"></i>
                  </NavigationMenu.Trigger>
                  <NavigationMenu.Content className="absolute top-full left-0 mt-2 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0 data-[state=open]:zoom-in-95 data-[state=closed]:zoom-out-95">
                    <div className="w-[720px] p-3 bg-gradient-to-br from-[#0D1512] via-[#0A0F0A] to-[#080C08] border border-[#E0EEEE]/20 rounded-xl shadow-[0_24px_50px_-12px_rgba(0,0,0,0.75),inset_0_1px_0_rgba(224,238,238,0.14)] grid grid-cols-3 gap-4">
                      <div>
                        <p className="text-[10px] font-bold text-[#E0EEEE]/45 uppercase tracking-[0.15em] mb-2 px-3 pt-1">Industry</p>
                        <ul className="grid gap-1">
                          {solutionsItems.map((item) => (
                            <NavListItem
                              key={item.title}
                              item={item}
                              onClick={handleNavItemClick}
                              isActive={item.href ? isActive(item.href) : false}
                            />
                          ))}
                        </ul>
                      </div>
                      <div>
                        <p className="text-[10px] font-bold text-[#E0EEEE]/45 uppercase tracking-[0.15em] mb-2 px-3 pt-1">Payment method</p>
                        <ul className="grid gap-1">
                          {paymentMethodItems.map((item) => (
                            <NavListItem
                              key={item.title}
                              item={item}
                              onClick={handleNavItemClick}
                              isActive={item.href ? isActive(item.href) : false}
                            />
                          ))}
                        </ul>
                      </div>
                      <div>
                        <p className="text-[10px] font-bold text-[#E0EEEE]/45 uppercase tracking-[0.15em] mb-2 px-3 pt-1">Failure reason</p>
                        <ul className="grid gap-1">
                          {failureReasonItems.map((item) => (
                            <NavListItem
                              key={item.title}
                              item={item}
                              onClick={handleNavItemClick}
                              isActive={item.href ? isActive(item.href) : false}
                            />
                          ))}
                        </ul>
                      </div>
                    </div>
                  </NavigationMenu.Content>
                </NavigationMenu.Item>

                {/* Integrations */}
                <NavigationMenu.Item>
                  <NavigationMenu.Link asChild>
                    <a
                      href="/integrations"
                      onClick={(e) => { e.preventDefault(); navigate('/integrations'); }}
                      className={`block px-3 py-2 text-sm font-medium transition-colors rounded-md cursor-pointer ${
                        isActive('/integrations') ? navActive : `${navText} ${navTextHover}`
                      }`}
                    >
                      Integrations
                    </a>
                  </NavigationMenu.Link>
                </NavigationMenu.Item>

                {/* Tools dropdown */}
                <NavigationMenu.Item className="relative">
                  <NavigationMenu.Trigger
                    className={`group flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-md transition-colors outline-none ${
                      isActiveGroup(['/tools']) ? navActive : `${navText} ${navTextHover}`
                    }`}
                  >
                    Tools
                    <i className="ph ph-caret-down text-xs transition-transform duration-200 group-data-[state=open]:rotate-180"></i>
                  </NavigationMenu.Trigger>
                  <NavigationMenu.Content className="absolute top-full left-0 mt-2 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0 data-[state=open]:zoom-in-95 data-[state=closed]:zoom-out-95">
                    <ul className="w-[420px] p-2 bg-gradient-to-br from-[#0D1512] via-[#0A0F0A] to-[#080C08] border border-[#E0EEEE]/20 rounded-xl shadow-[0_24px_50px_-12px_rgba(0,0,0,0.75),inset_0_1px_0_rgba(224,238,238,0.14)] grid gap-1">
                      {toolsItems.map((item) => (
                        <NavListItem
                          key={item.title}
                          item={item}
                          onClick={handleNavItemClick}
                          isActive={item.href ? isActive(item.href) : false}
                        />
                      ))}
                    </ul>
                  </NavigationMenu.Content>
                </NavigationMenu.Item>

                {/* Docs */}
                <NavigationMenu.Item>
                  <NavigationMenu.Link asChild>
                    <a
                      href="https://docs.revtain.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`block px-3 py-2 text-sm font-medium ${navText} ${navTextHover} transition-colors rounded-md cursor-pointer`}
                    >
                      Docs
                    </a>
                  </NavigationMenu.Link>
                </NavigationMenu.Item>

                {/* Pricing (scroll link) */}
                <NavigationMenu.Item>
                  <NavigationMenu.Link asChild>
                    <a
                      href="#section-performance"
                      onClick={(e) => scrollToSection(e, 'section-performance')}
                      className={`block px-3 py-2 text-sm font-medium ${navText} ${navTextHover} transition-colors rounded-md cursor-pointer`}
                    >
                      Pricing
                    </a>
                  </NavigationMenu.Link>
                </NavigationMenu.Item>

                {/* Company dropdown */}
                <NavigationMenu.Item className="relative">
                  <NavigationMenu.Trigger
                    className={`group flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-md transition-colors outline-none ${
                      isActiveGroup(['/about', '/blog']) ? navActive : `${navText} ${navTextHover}`
                    }`}
                  >
                    Company
                    <i className="ph ph-caret-down text-xs transition-transform duration-200 group-data-[state=open]:rotate-180"></i>
                  </NavigationMenu.Trigger>
                  <NavigationMenu.Content className="absolute top-full right-0 mt-2 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0 data-[state=open]:zoom-in-95 data-[state=closed]:zoom-out-95">
                    <ul className="w-[420px] p-2 bg-gradient-to-br from-[#0D1512] via-[#0A0F0A] to-[#080C08] border border-[#E0EEEE]/20 rounded-xl shadow-[0_24px_50px_-12px_rgba(0,0,0,0.75),inset_0_1px_0_rgba(224,238,238,0.14)] grid gap-1">
                      {companyItems.map((item) => (
                        <NavListItem
                          key={item.title}
                          item={item}
                          onClick={handleNavItemClick}
                          isActive={item.href ? isActive(item.href) : false}
                        />
                      ))}
                    </ul>
                  </NavigationMenu.Content>
                </NavigationMenu.Item>
              </NavigationMenu.List>
            </NavigationMenu.Root>

            {/* CTA */}
            <a
              href="#section-contact"
              onClick={(e) => scrollToSection(e, 'section-contact')}
              className="ml-3 inline-flex items-center gap-2 bg-gradient-to-r from-[#00A372] to-[#008B5E] hover:from-[#00B880] hover:to-[#009968] text-white px-6 py-2.5 rounded-lg text-sm font-bold transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-[#00A372]/30 whitespace-nowrap cursor-pointer btn-shine btn-press"
            >
              Get Free Audit
              <i className="ph ph-arrow-right"></i>
            </a>
          </div>

          {/* Mobile hamburger */}
          <button
            className={`md:hidden w-10 h-10 flex items-center justify-center ${mobileTriggerText} ${navTextHover} transition-colors cursor-pointer`}
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            <i className={`ph ${isMobileMenuOpen ? 'ph-x' : 'ph-list'} text-2xl`}></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-[#0A0F0A]/98 backdrop-blur-md border-t border-[#E0EEEE]/10 max-h-[80vh] overflow-y-auto">
          <div className="px-6 py-6 space-y-1">
            <a
              href="#section-product"
              onClick={(e) => scrollToSection(e, 'section-product')}
              className="block py-2.5 text-sm font-medium text-[#E0EEEE] hover:text-[#00E599] transition-colors duration-200 cursor-pointer"
            >
              Product
            </a>

            <div className="py-2">
              <p className="text-xs font-bold text-[#E0EEEE]/30 uppercase tracking-wider mb-2">Solutions · Industry</p>
              {solutionsItems.filter(i => i.href).map((item) => (
                <a
                  key={item.title}
                  href={item.href}
                  onClick={(e) => { e.preventDefault(); navigate(item.href!); setIsMobileMenuOpen(false); }}
                  className={`block py-2 pl-3 text-sm font-medium transition-colors ${isActive(item.href!) ? 'text-[#00E599]' : 'text-[#E0EEEE]/70 hover:text-[#00E599]'}`}
                >
                  {item.title}
                </a>
              ))}
            </div>

            <div className="py-2">
              <p className="text-xs font-bold text-[#E0EEEE]/30 uppercase tracking-wider mb-2">Payment method</p>
              {paymentMethodItems.map((item) => (
                <a
                  key={item.title}
                  href={item.href}
                  onClick={(e) => { e.preventDefault(); navigate(item.href!); setIsMobileMenuOpen(false); }}
                  className={`block py-2 pl-3 text-sm font-medium transition-colors ${isActive(item.href!) ? 'text-[#00E599]' : 'text-[#E0EEEE]/70 hover:text-[#00E599]'}`}
                >
                  {item.title}
                </a>
              ))}
            </div>

            <div className="py-2">
              <p className="text-xs font-bold text-[#E0EEEE]/30 uppercase tracking-wider mb-2">Failure reason</p>
              {failureReasonItems.map((item) => (
                <a
                  key={item.title}
                  href={item.href}
                  onClick={(e) => { e.preventDefault(); navigate(item.href!); setIsMobileMenuOpen(false); }}
                  className={`block py-2 pl-3 text-sm font-medium transition-colors ${isActive(item.href!) ? 'text-[#00E599]' : 'text-[#E0EEEE]/70 hover:text-[#00E599]'}`}
                >
                  {item.title}
                </a>
              ))}
            </div>

            <a
              href="/integrations"
              onClick={(e) => { e.preventDefault(); navigate('/integrations'); setIsMobileMenuOpen(false); }}
              className={`block py-2.5 text-sm font-medium transition-colors ${isActive('/integrations') ? navActive : `${navText} ${navTextHover}`}`}
            >
              Integrations
            </a>

            <div className="py-2">
              <p className="text-xs font-bold text-[#E0EEEE]/30 uppercase tracking-wider mb-2">Tools</p>
              {toolsItems.map((item) => (
                <a
                  key={item.title}
                  href={item.href}
                  onClick={(e) => { e.preventDefault(); navigate(item.href!); setIsMobileMenuOpen(false); }}
                  className={`block py-2 pl-3 text-sm font-medium transition-colors ${isActive(item.href!) ? 'text-[#00E599]' : 'text-[#E0EEEE]/70 hover:text-[#00E599]'}`}
                >
                  {item.title}
                </a>
              ))}
            </div>

            <a
              href="https://docs.revtain.com"
              target="_blank"
              rel="noopener noreferrer"
              className="block py-2.5 text-sm font-medium text-[#E0EEEE] hover:text-[#00E599] transition-colors"
            >
              Docs
            </a>

            <a
              href="#section-performance"
              onClick={(e) => scrollToSection(e, 'section-performance')}
              className="block py-2.5 text-sm font-medium text-[#E0EEEE] hover:text-[#00E599] transition-colors duration-200 cursor-pointer"
            >
              Pricing
            </a>

            <div className="py-2">
              <p className="text-xs font-bold text-[#E0EEEE]/30 uppercase tracking-wider mb-2">Company</p>
              {companyItems.map((item) => (
                <a
                  key={item.title}
                  href={item.href || `#${item.scrollTo}`}
                  onClick={(e) => {
                    if (item.scrollTo) {
                      scrollToSection(e, item.scrollTo);
                    } else if (item.href) {
                      e.preventDefault();
                      navigate(item.href);
                      setIsMobileMenuOpen(false);
                    }
                  }}
                  className={`block py-2 pl-3 text-sm font-medium transition-colors ${item.href && isActive(item.href) ? 'text-[#00E599]' : 'text-[#E0EEEE]/70 hover:text-[#00E599]'}`}
                >
                  {item.title}
                </a>
              ))}
            </div>

            <div className="pt-3">
              <a
                href="#section-contact"
                onClick={(e) => scrollToSection(e, 'section-contact')}
                className="inline-flex items-center gap-2 bg-gradient-to-r from-[#00A372] to-[#008B5E] hover:from-[#00B880] hover:to-[#009968] text-white px-6 py-2.5 rounded-lg text-sm font-bold transition-all duration-300 whitespace-nowrap cursor-pointer w-full justify-center"
              >
                Get Free Audit
                <i className="ph ph-arrow-right"></i>
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}

interface NavListItemProps {
  item: NavItem;
  onClick: (e: React.MouseEvent, item: NavItem) => void;
  isActive: boolean;
}

function NavListItem({ item, onClick, isActive }: NavListItemProps) {
  return (
    <li>
      <NavigationMenu.Link asChild>
        <a
          href={item.href || `#${item.scrollTo}`}
          onClick={(e) => onClick(e, item)}
          target={item.external ? '_blank' : undefined}
          rel={item.external ? 'noopener noreferrer' : undefined}
          className={`flex items-start gap-3 p-3 rounded-lg transition-all duration-200 group ${
            isActive
              ? 'bg-[#00E599]/[0.08] border border-[#00E599]/20'
              : 'border border-transparent hover:bg-[#E0EEEE]/[0.04] hover:border-[#E0EEEE]/10'
          }`}
        >
          <div
            className={`flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center transition-all ${
              isActive
                ? 'bg-gradient-to-br from-[#00E599] to-[#00A372] shadow-md shadow-[#00E599]/20'
                : 'bg-[#E0EEEE]/[0.04] border border-[#E0EEEE]/10 group-hover:bg-[#00E599]/10 group-hover:border-[#00E599]/30'
            }`}
          >
            <i
              className={`ph-fill ${item.icon} text-base ${
                isActive ? 'text-[#001A1A]' : 'text-[#00E599]'
              }`}
            ></i>
          </div>
          <div className="flex flex-col gap-0.5 min-w-0">
            <div
              className={`text-sm font-semibold leading-tight ${
                isActive ? 'text-[#00E599]' : 'text-[#E0EEEE] group-hover:text-[#00E599]'
              }`}
            >
              {item.title}
            </div>
            <div className="text-xs text-[#E0EEEE]/45 leading-snug line-clamp-2">
              {item.description}
            </div>
          </div>
        </a>
      </NavigationMenu.Link>
    </li>
  );
}
